import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, Edit3, Camera, Eye, MessageSquare, X, Calendar, Users, Truck, Wrench, Euro, FileUp, Send, Download, Paperclip, Building2, TrendingUp, BarChart3, Printer, ExternalLink } from 'lucide-react';

const STATUS_MAP = {
  planeamento: { label: 'Planeamento', cls: 'badge-info' },
  em_curso: { label: 'Em Curso', cls: 'badge-warning' },
  concluido: { label: 'Concluido', cls: 'badge-success' },
  suspenso: { label: 'Suspenso', cls: 'badge-error' }
};

function CurvaABC({ projectId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${projectId}/curva-abc`).then(r => setData(r.data)).catch(() => {}); }, [projectId]);
  if (!data || !data.items?.length) return <div className="pro-card-padded text-center py-8"><BarChart3 size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} /><p className="font-semibold">Sem dados para Curva ABC</p><p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Registe custos ou converta um orcamento com itens</p></div>;

  const colors = { A: { bg: '#FDECEB', border: '#e53e3e', text: '#9B2C2C' }, B: { bg: '#FFF4E0', border: '#ed8936', text: '#8B6914' }, C: { bg: '#E8F5EC', border: '#48bb78', text: '#276749' } };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3" data-testid="abc-summary">
        {['A', 'B', 'C'].map(cls => {
          const items = data[cls.toLowerCase()] || [];
          const total = items.reduce((s, i) => s + i.amount, 0);
          const c = colors[cls];
          return (
            <div key={cls} className="p-3 rounded-md" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-lg font-bold" style={{ color: c.text }}>Classe {cls}</span>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ background: c.border, color: 'white' }}>{items.length} itens</span>
              </div>
              <p className="text-sm font-bold" style={{ color: c.text }}>{total.toLocaleString('pt-PT')} EUR</p>
              <p className="text-[10px]" style={{ color: c.text }}>{cls === 'A' ? '~80% do custo total' : cls === 'B' ? '~15% do custo total' : '~5% do custo total'}</p>
            </div>
          );
        })}
      </div>
      <div className="pro-card-padded" data-testid="abc-pareto">
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Distribuicao Pareto</h3>
        <div className="w-full h-6 rounded-full overflow-hidden flex" style={{ background: 'var(--border)' }}>
          {data.items.map((it, i) => {
            const c = colors[it.class];
            return <div key={i} title={`${it.description}: ${it.amount} EUR (${it.percentage}%)`} style={{ width: `${it.percentage}%`, background: c.border, minWidth: it.percentage > 0.5 ? '2px' : '0' }} />;
          })}
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-[10px]" style={{ color: 'var(--text-light)' }}>0%</span>
          <span className="text-[10px]" style={{ color: 'var(--text-light)' }}>100%</span>
        </div>
      </div>
      <div className="pro-card overflow-hidden" data-testid="abc-table">
        <div className="overflow-x-auto">
          <table className="pro-table">
            <thead><tr><th>Classe</th><th>Descricao</th><th>Categoria</th><th>Valor</th><th>%</th><th>% Acum.</th></tr></thead>
            <tbody>
              {data.items.map((it, i) => {
                const c = colors[it.class];
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafbfc' }}>
                    <td><span className="text-xs font-bold px-2 py-0.5 rounded" style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}` }}>{it.class}</span></td>
                    <td className="text-xs font-medium">{it.description}</td>
                    <td><span className="badge badge-neutral">{it.category}</span></td>
                    <td className="font-semibold">{it.amount.toLocaleString('pt-PT')} EUR</td>
                    <td className="text-xs">{it.percentage}%</td>
                    <td className="text-xs font-medium">{it.cumulative_pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-right"><span className="text-sm font-bold" style={{ color: 'var(--primary)' }}>Total: {data.total.toLocaleString('pt-PT')} EUR</span></div>
    </div>
  );
}

function ProjectDetail({ project: initialProject, onBack, employees, vehicles }) {
  const [project, setProject] = useState(initialProject);
  const [tab, setTab] = useState('info');
  const [form, setForm] = useState(initialProject);

  // Cost form
  const [costForm, setCostForm] = useState({ category: 'material', description: '', amount: 0, date: '', invoice_ref: '', notes: '' });
  // Task form
  const [taskForm, setTaskForm] = useState({ task: '', start_date: '', end_date: '', status: 'pendente', progress: 0, estimated_days: 0 });
  const [timelineMode, setTimelineMode] = useState('dates'); // 'dates' | 'days'
  // Photo
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoVisible, setPhotoVisible] = useState(false);
  // Message
  const [msgText, setMsgText] = useState('');
  const [msgVisible, setMsgVisible] = useState(true);
  // File
  const [fileVisible, setFileVisible] = useState(false);
  // Assign
  const [assignType, setAssignType] = useState('employee');
  const [assignId, setAssignId] = useState('');

  const refresh = async () => {
    const { data } = await api.get(`/projects/${project.id}`);
    setProject(data);
    setForm(data);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const saveInfo = async () => {
    await api.put(`/projects/${project.id}`, {
      name: form.name, status: form.status, client_name: form.client_name,
      location: form.location, start_date: form.start_date, end_date: form.end_date,
      description: form.description, budget_total: form.budget_total
    });
    refresh();
  };

  const addCost = async () => {
    await api.post(`/projects/${project.id}/costs`, costForm);
    setCostForm({ category: 'material', description: '', amount: 0, date: '', invoice_ref: '', notes: '' });
    refresh();
  };

  const deleteCost = async (costId) => {
    await api.delete(`/projects/${project.id}/costs/${costId}`);
    refresh();
  };

  const addTask = async () => {
    let taskData = { ...taskForm };
    // If days mode and start_date not provided, auto-calculate from project start_date + previous tasks
    if (timelineMode === 'days' && taskData.estimated_days > 0) {
      const startDate = project.start_date ? new Date(project.start_date) : new Date();
      // Sum days from existing tasks
      const existingDays = (project.timeline || []).reduce((s, t) => s + (t.estimated_days || 0), 0);
      const taskStart = new Date(startDate);
      taskStart.setDate(taskStart.getDate() + existingDays);
      const taskEnd = new Date(taskStart);
      taskEnd.setDate(taskEnd.getDate() + taskData.estimated_days);
      taskData.start_date = taskStart.toISOString().split('T')[0];
      taskData.end_date = taskEnd.toISOString().split('T')[0];
    }
    await api.post(`/projects/${project.id}/timeline`, taskData);
    setTaskForm({ task: '', start_date: '', end_date: '', status: 'pendente', progress: 0, estimated_days: 0 });
    refresh();
  };

  const updateTaskStatus = async (taskId, status, progress) => {
    await api.put(`/projects/${project.id}/timeline/${taskId}`, { status, progress });
    refresh();
  };

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      await api.post(`/projects/${project.id}/photos`, { image_base64: ev.target.result.split(',')[1], caption: photoCaption, client_visible: photoVisible });
      setPhotoCaption('');
      refresh();
    };
    reader.readAsDataURL(file);
  };

  const addFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      await api.post(`/projects/${project.id}/files`, { name: file.name, data: ev.target.result.split(',')[1], mime_type: file.type, size: file.size, client_visible: fileVisible });
      refresh();
    };
    reader.readAsDataURL(file);
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    await api.post(`/projects/${project.id}/messages`, { text: msgText, client_visible: msgVisible });
    setMsgText('');
    refresh();
  };

  const assignToProject = async () => {
    if (!assignId) return;
    const list = assignType === 'employee' ? employees : vehicles;
    const item = list.find(i => i.id === assignId);
    if (!item) return;
    await api.post(`/projects/${project.id}/assign`, { type: assignType, item_id: item.id, name: item.name || item.plate || '' });
    setAssignId('');
    refresh();
  };

  const unassign = async (type, itemId) => {
    await api.post(`/projects/${project.id}/unassign`, { type, item_id: itemId });
    refresh();
  };

  const handlePrintCronograma = (target = 'obra') => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backendUrl}/api/projects/${project.id}/cronograma-pdf?target=${target}`, '_blank');
  };

  const handlePrintCronogramaWindow = () => {
    // Print the Gantt chart in a new window
    const tasks = project.timeline || [];
    if (tasks.length === 0) return;
    const statusLabel = (s) => s === 'concluido' ? 'Concluida' : s === 'em_curso' ? 'Em Curso' : 'Pendente';
    const statusColor = (s) => s === 'concluido' ? '#0B7A3E' : s === 'em_curso' ? '#C67A09' : '#94A3B8';
    const rows = tasks.map((t, i) => {
      const d = t.start_date && t.end_date ? Math.max(0, Math.ceil((new Date(t.end_date) - new Date(t.start_date)) / 86400000)) : (t.estimated_days || 0);
      return `<tr style="background:${i%2===0?'#f7f9fc':'#fff'}">
        <td>${i+1}</td>
        <td>${t.task}</td>
        <td>${t.start_date || '—'}</td>
        <td>${t.end_date || '—'}</td>
        <td>${d}d</td>
        <td style="color:${statusColor(t.status)};font-weight:bold">${statusLabel(t.status)}</td>
        <td>${t.status === 'concluido' ? '100%' : (t.progress || 0) + '%'}</td>
        <td></td>
      </tr>`;
    }).join('');
    const win = window.open('', '_blank');
    win.document.write(`
      <!DOCTYPE html><html><head><title>Cronograma - ${project.name}</title>
      <meta charset="utf-8">
      <style>
        body{font-family:Arial,sans-serif;padding:20px;color:#1a202c}
        h1{color:#0F2B4E;font-size:20px;margin-bottom:4px}
        .subtitle{color:#5A6B7D;font-size:12px;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th{background:#0F2B4E;color:#fff;padding:6px 8px;text-align:left}
        td{padding:5px 8px;border-bottom:1px solid #e2e8f0}
        .footer{margin-top:24px;font-size:10px;color:#94a3b8;text-align:center}
        @media print{button{display:none}}
      </style></head><body>
      <h1>Cronograma de Obra</h1>
      <div class="subtitle">${project.name} | ${project.client_name || ''} | ${project.location || ''} | Inicio: ${project.start_date || 'A definir'} | Prazo: ${project.estimated_days || '?'} dias</div>
      <button onclick="window.print()" style="margin-bottom:16px;padding:8px 16px;background:#1A5FAB;color:white;border:none;border-radius:4px;cursor:pointer">Imprimir</button>
      <table>
        <thead><tr><th>#</th><th>Tarefa</th><th>Inicio</th><th>Conclusao</th><th>Dias</th><th>Estado</th><th>Progresso</th><th>Notas</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="footer">PRUMO software | Criado por: Rui Guimaraes | Documento gerado em ${new Date().toLocaleDateString('pt-PT')}</div>
      </body></html>`);
    win.document.close();
  };

  const totalCosts = (project.actual_costs || []).reduce((s, c) => s + (c.amount || 0), 0);
  const budgetRemaining = (project.budget_total || 0) - totalCosts;

  const tabs = [
    { id: 'info', label: 'Informacao', icon: Edit3 },
    { id: 'costs', label: 'Custos', icon: Euro },
    { id: 'abc', label: 'Curva ABC', icon: TrendingUp },
    { id: 'team', label: 'Equipa & Recursos', icon: Users },
    { id: 'timeline', label: 'Cronograma', icon: Calendar },
    { id: 'photos', label: 'Fotos', icon: Camera },
    { id: 'files', label: 'Ficheiros', icon: FileUp },
    { id: 'messages', label: 'Mensagens', icon: MessageSquare },
    { id: 'portal', label: 'Portal', icon: Eye },
  ];

  // Calculate progress percentage from tasks
  const completedTasks = (project.timeline || []).filter(t => t.status === 'concluido').length;
  const totalTasks = (project.timeline || []).length;
  const progressPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  
  // Vehicle costs
  const vehicleCosts = (project.assigned_vehicles || []).reduce((s, v) => s + ((v.hourly_rate || 0) * (v.actual_hours || 0)), 0);

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={onBack} className="text-xs font-medium cursor-pointer mb-1" style={{ color: 'var(--text-light)', background: 'none', border: 'none' }}>&#8592; Voltar aos projetos</button>
          <h1 className="text-xl font-bold">{project.name}</h1>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{project.client_name} - {project.location}</p>
        </div>
        <span className={`badge ${(STATUS_MAP[project.status] || STATUS_MAP.planeamento).cls}`}>{(STATUS_MAP[project.status] || STATUS_MAP.planeamento).label}</span>
      </div>

      {/* Dashboard Summary - Budget vs Cost + Progress */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="metric-card">
          <p className="metric-label">Orcamento Inicial</p>
          <p className="metric-value text-lg">{(project.budget_total || 0).toLocaleString('pt-PT')} EUR</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Custo Real</p>
          <p className="metric-value text-lg" style={{ color: 'var(--error)' }}>{(totalCosts + vehicleCosts).toLocaleString('pt-PT')} EUR</p>
          <p className="text-[9px]" style={{ color: 'var(--text-light)' }}>Materiais: {totalCosts.toLocaleString('pt-PT')} | Frota: {vehicleCosts.toLocaleString('pt-PT')}</p>
        </div>
        <div className="metric-card">
          <p className="metric-label">Saldo</p>
          <p className="metric-value text-lg" style={{ color: (budgetRemaining - vehicleCosts) >= 0 ? 'var(--success)' : 'var(--error)' }}>
            {(budgetRemaining - vehicleCosts).toLocaleString('pt-PT')} EUR
          </p>
        </div>
        <div className="metric-card">
          <p className="metric-label">% Concluida</p>
          <p className="metric-value text-lg" style={{ color: progressPct === 100 ? 'var(--success)' : 'var(--info)' }}>{progressPct}%</p>
          <div className="w-full h-1.5 rounded-full mt-1" style={{ background: 'var(--border)' }}>
            <div className="h-1.5 rounded-full transition-all" style={{ width: `${progressPct}%`, background: progressPct === 100 ? 'var(--success)' : 'var(--info)' }} />
          </div>
        </div>
        <div className="metric-card">
          <p className="metric-label">Tarefas</p>
          <p className="metric-value text-lg">{completedTasks}/{totalTasks}</p>
          <p className="text-[9px]" style={{ color: 'var(--text-light)' }}>{totalTasks - completedTasks} pendentes</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 mb-4 overflow-x-auto" style={{ borderBottom: '1px solid var(--border)' }}>
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`project-tab-${id}`} onClick={() => setTab(id)} className={`tab-btn flex items-center gap-1.5 ${tab === id ? 'active' : ''}`}>
            <Icon size={14} /> <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* INFO */}
      {tab === 'info' && (
        <div className="pro-card-padded">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="field-label">Nome</label><input className="input" value={form.name || ''} onChange={e => set('name', e.target.value)} /></div>
            <div><label className="field-label">Estado</label>
              <select className="select" value={form.status || 'planeamento'} onChange={e => set('status', e.target.value)}>
                <option value="planeamento">Planeamento</option><option value="em_curso">Em Curso</option><option value="concluido">Concluido</option><option value="suspenso">Suspenso</option>
              </select></div>
            <div><label className="field-label">Cliente</label><input className="input" value={form.client_name || ''} onChange={e => set('client_name', e.target.value)} /></div>
            <div><label className="field-label">Local</label><input className="input" value={form.location || ''} onChange={e => set('location', e.target.value)} /></div>
            <div><label className="field-label">Data Inicio</label><input className="input" type="date" value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} /></div>
            <div><label className="field-label">Data Fim</label><input className="input" type="date" value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} /></div>
            <div className="md:col-span-2"><label className="field-label">Descricao</label><textarea className="input" rows={3} value={form.description || ''} onChange={e => set('description', e.target.value)} /></div>
          </div>
          <div className="flex justify-end mt-4"><button data-testid="save-project-btn" onClick={saveInfo} className="btn btn-primary">Guardar</button></div>
        </div>
      )}

      {/* COSTS */}
      {tab === 'costs' && (
        <div className="pro-card-padded">
          <h3 className="text-sm font-semibold mb-3">Registar Custo Real</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <select className="select" value={costForm.category} onChange={e => setCostForm(p => ({ ...p, category: e.target.value }))}>
              <option value="material">Material</option><option value="mao_obra">Mao de Obra</option><option value="equipamento">Equipamento</option><option value="subempreitada">Subempreitada</option><option value="transporte">Transporte</option><option value="licenca">Licenca</option><option value="outro">Outro</option>
            </select>
            <input className="input" placeholder="Descricao" value={costForm.description} onChange={e => setCostForm(p => ({ ...p, description: e.target.value }))} />
            <input className="input" type="number" placeholder="Valor EUR" value={costForm.amount} onChange={e => setCostForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} />
            <input className="input" type="date" value={costForm.date} onChange={e => setCostForm(p => ({ ...p, date: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <input className="input" placeholder="Ref. Fatura" value={costForm.invoice_ref} onChange={e => setCostForm(p => ({ ...p, invoice_ref: e.target.value }))} />
            <button data-testid="add-cost-btn" onClick={addCost} className="btn btn-primary"><Plus size={14} /> Registar</button>
          </div>

          {/* Cost summary */}
          {(project.actual_costs?.length > 0 || project.estimated_costs?.length > 0) && (
            <div className="grid grid-cols-3 gap-3 mb-4 p-3 rounded-lg" style={{ background: 'var(--surface-alt)' }}>
              <div className="text-center">
                <p className="text-[10px] font-medium mb-0.5" style={{ color: 'var(--text-secondary)' }}>Orcamento</p>
                <p className="text-sm font-bold" style={{ color: 'var(--accent)' }}>{(project.budget_total || 0).toLocaleString('pt-PT')} EUR</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium mb-0.5" style={{ color: 'var(--text-secondary)' }}>Gasto Real</p>
                <p className="text-sm font-bold" style={{ color: 'var(--error)' }}>{(project.total_spent || 0).toLocaleString('pt-PT')} EUR</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-medium mb-0.5" style={{ color: 'var(--text-secondary)' }}>Saldo</p>
                <p className="text-sm font-bold" style={{ color: (project.budget_total - project.total_spent) >= 0 ? 'var(--success)' : 'var(--error)' }}>
                  {((project.budget_total || 0) - (project.total_spent || 0)).toLocaleString('pt-PT')} EUR
                </p>
              </div>
            </div>
          )}

          {project.actual_costs?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="pro-table">
                <thead><tr><th>Data</th><th>Categoria</th><th>Descricao</th><th>Fatura</th><th>Valor</th><th></th></tr></thead>
                <tbody>
                  {project.actual_costs.map(c => (
                    <tr key={c.id}>
                      <td className="text-xs">{c.date}</td>
                      <td><span className="badge badge-neutral">{c.category}</span></td>
                      <td className="text-xs">{c.description}</td>
                      <td className="text-xs">{c.invoice_ref}</td>
                      <td className="font-semibold">{(c.amount || 0).toLocaleString('pt-PT')} EUR</td>
                      <td><button onClick={() => deleteCost(c.id)} className="btn btn-danger btn-xs"><Trash2 size={12} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem custos reais registados</p>}

          {/* Estimated costs from budget - with date calculation */}
          {project.estimated_costs?.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Custos Estimados do Orcamento Original</h3>
                <span className="badge badge-info">Do orcamento</span>
              </div>
              <div className="overflow-x-auto">
                <table className="pro-table">
                  <thead>
                    <tr>
                      <th>Descricao</th>
                      <th>Categoria</th>
                      <th>Data Estimada</th>
                      <th>Valor Estimado</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.estimated_costs.map((c, idx) => {
                      // Calculate estimated date from project start_date + offset
                      let estimatedDate = c.estimated_date || '';
                      if (!estimatedDate && project.start_date) {
                        const start = new Date(project.start_date);
                        const offset = Math.round(idx * (project.estimated_days || 30) / Math.max(project.estimated_costs.length, 1));
                        const d = new Date(start);
                        d.setDate(d.getDate() + offset);
                        estimatedDate = d.toISOString().split('T')[0];
                      }
                      const isLate = estimatedDate && new Date(estimatedDate) < new Date() && project.status !== 'concluido';
                      return (
                        <tr key={c.id} style={{ background: 'var(--surface-alt)' }}>
                          <td className="text-xs">{c.description}</td>
                          <td><span className="badge badge-neutral">{c.category}</span></td>
                          <td className="text-xs">
                            {estimatedDate ? (
                              <span style={{ color: isLate ? 'var(--error)' : 'var(--text-secondary)' }}>
                                {estimatedDate} {isLate ? '⚠ em atraso' : ''}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="font-semibold" style={{ color: 'var(--accent)' }}>{(c.amount || 0).toLocaleString('pt-PT')} EUR</td>
                          <td>
                            {/* Check if there's a matching actual cost */}
                            {project.actual_costs?.some(ac => ac.description?.toLowerCase().includes((c.description || '').toLowerCase().slice(0,15))) ? (
                              <span className="badge badge-success">Registado</span>
                            ) : (
                              <span className="badge badge-neutral">Pendente</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-right mt-1">
                <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
                  Total Estimado: {project.estimated_costs.reduce((s, c) => s + (c.amount || 0), 0).toLocaleString('pt-PT')} EUR
                </span>
              </div>
            </div>
          )}

          {/* Budget items if no estimated costs */}
          {!project.estimated_costs?.length && project.budget_items?.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Itens do Orcamento Original</h3>
              <div className="overflow-x-auto">
                <table className="pro-table">
                  <thead><tr><th>Descricao</th><th>Qtd</th><th>P. Unit.</th><th>Subtotal</th></tr></thead>
                  <tbody>
                    {project.budget_items.map((it, i) => (
                      <tr key={i} style={{ background: 'var(--surface-alt)' }}>
                        <td className="text-xs">{it.descricao}</td>
                        <td>{it.quantidade} {it.unidade}</td>
                        <td>{(it.preco_unitario || 0).toFixed(2)} EUR</td>
                        <td className="font-semibold" style={{ color: 'var(--text-secondary)' }}>{(it.subtotal || 0).toFixed(2)} EUR</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* CURVA ABC */}
      {tab === 'abc' && <CurvaABC projectId={project.id} />}

      {/* TEAM & RESOURCES */}
      {tab === 'team' && (
        <div className="pro-card-padded">
          <h3 className="text-sm font-semibold mb-3">Associar Recurso</h3>
          <div className="flex gap-2 mb-4 flex-wrap">
            <select className="select" style={{ width: 'auto' }} value={assignType} onChange={e => setAssignType(e.target.value)}>
              <option value="employee">Funcionario</option><option value="vehicle">Veiculo/Frota</option>
            </select>
            <select className="select flex-1" value={assignId} onChange={e => setAssignId(e.target.value)}>
              <option value="">Selecionar...</option>
              {(assignType === 'employee' ? employees : vehicles).map(i => (
                <option key={i.id} value={i.id}>{i.name || i.plate} {i.role ? `(${i.role})` : ''}</option>
              ))}
            </select>
            {assignType === 'vehicle' && (
              <>
                <input className="input" type="number" placeholder="EUR/hora" style={{ width: '100px' }} id="vehicle-hourly-rate" />
                <input className="input" type="number" placeholder="Horas est." style={{ width: '100px' }} id="vehicle-est-hours" />
              </>
            )}
            <button data-testid="assign-btn" onClick={async () => {
              if (!assignId) return;
              const list = assignType === 'employee' ? employees : vehicles;
              const item = list.find(i => i.id === assignId);
              if (!item) return;
              const data = { type: assignType, item_id: item.id, name: item.name || `${item.plate} ${item.brand || ''} ${item.model || ''}`.trim() };
              if (assignType === 'vehicle') {
                data.hourly_rate = parseFloat(document.getElementById('vehicle-hourly-rate')?.value) || 0;
                data.estimated_hours = parseFloat(document.getElementById('vehicle-est-hours')?.value) || 0;
              }
              await api.post(`/projects/${project.id}/assign`, data);
              setAssignId('');
              if (document.getElementById('vehicle-hourly-rate')) document.getElementById('vehicle-hourly-rate').value = '';
              if (document.getElementById('vehicle-est-hours')) document.getElementById('vehicle-est-hours').value = '';
              refresh();
            }} className="btn btn-primary btn-sm"><Plus size={14} /> Associar</button>
          </div>
          
          {/* Custo total de veículos */}
          {project.assigned_vehicles?.length > 0 && (
            <div className="p-3 rounded-md mb-4" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>Custo Total Frota na Obra:</span>
                <span className="text-sm font-bold" style={{ color: 'var(--primary)' }}>
                  {project.assigned_vehicles.reduce((s, v) => s + (v.total_cost || (v.hourly_rate || 0) * (v.actual_hours || 0)), 0).toLocaleString('pt-PT')} EUR
                </span>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold mb-2 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}><Users size={14} /> Funcionarios ({project.assigned_employees?.length || 0})</h4>
              {project.assigned_employees?.length > 0 ? project.assigned_employees.map(e => (
                <div key={e.id} className="flex items-center justify-between p-2 rounded-md mb-1" style={{ background: 'var(--surface-alt)' }}>
                  <span className="text-sm font-medium">{e.name}</span>
                  <button onClick={() => unassign('employee', e.id)} className="btn btn-danger btn-xs"><X size={12} /></button>
                </div>
              )) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem funcionarios</p>}
            </div>
            <div>
              <h4 className="text-xs font-semibold mb-2 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}><Truck size={14} /> Frota ({project.assigned_vehicles?.length || 0})</h4>
              {project.assigned_vehicles?.length > 0 ? project.assigned_vehicles.map(v => (
                <div key={v.id} className="p-3 rounded-md mb-2" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">{v.name}</span>
                    <button onClick={() => unassign('vehicle', v.id)} className="btn btn-danger btn-xs"><X size={12} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>EUR/Hora</label>
                      <input className="input text-xs" type="number" defaultValue={v.hourly_rate || 0} 
                        onBlur={async (e) => {
                          await api.put(`/projects/${project.id}/vehicles/${v.id}`, { hourly_rate: parseFloat(e.target.value) || 0 });
                          refresh();
                        }} />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Horas Reais</label>
                      <input className="input text-xs" type="number" defaultValue={v.actual_hours || 0}
                        onBlur={async (e) => {
                          await api.put(`/projects/${project.id}/vehicles/${v.id}`, { actual_hours: parseFloat(e.target.value) || 0 });
                          refresh();
                        }} />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Custo</label>
                      <p className="text-xs font-bold mt-1" style={{ color: 'var(--primary)' }}>
                        {((v.hourly_rate || 0) * (v.actual_hours || 0)).toLocaleString('pt-PT')} EUR
                      </p>
                    </div>
                  </div>
                </div>
              )) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem veiculos</p>}
            </div>
          </div>
        </div>
      )}

      {/* TIMELINE */}
      {tab === 'timeline' && (
        <div className="pro-card-padded">
          {/* Estimated vs Actual summary */}
          {(project.estimated_days || project.start_date || project.end_date) && (
            <div className="p-3 rounded-md mb-4" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
              <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Resumo do Cronograma</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Dias Estimados</p>
                  <p className="text-sm font-bold" style={{ color: 'var(--info)' }}>{project.estimated_days || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Dias Reais</p>
                  <p className="text-sm font-bold" style={{ color: (() => {
                    if (!project.start_date) return 'var(--text-secondary)';
                    const start = new Date(project.start_date);
                    const end = project.end_date ? new Date(project.end_date) : new Date();
                    const actual = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                    return actual > (project.estimated_days || 999) ? 'var(--error)' : 'var(--success)';
                  })() }}>
                    {project.start_date ? (() => {
                      const start = new Date(project.start_date);
                      const end = project.end_date ? new Date(project.end_date) : new Date();
                      return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                    })() : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Inicio</p>
                  <p className="text-sm font-semibold">{project.start_date || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>Fim {project.end_date ? '' : '(previsto)'}</p>
                  <p className="text-sm font-semibold">{project.end_date || '-'}</p>
                </div>
              </div>
              {project.start_date && project.estimated_days > 0 && (() => {
                const start = new Date(project.start_date);
                const end = project.end_date ? new Date(project.end_date) : new Date();
                const actual = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
                const pct = Math.min(100, Math.round((actual / project.estimated_days) * 100));
                const overBudget = actual > project.estimated_days;
                return (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-[10px] font-medium mb-1">
                      <span style={{ color: 'var(--text-light)' }}>Progresso temporal</span>
                      <span style={{ color: overBudget ? 'var(--error)' : 'var(--success)' }}>{pct}% {overBudget ? '(atrasado)' : ''}</span>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ background: 'var(--border)' }}>
                      <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: overBudget ? 'var(--error)' : 'var(--success)' }} />
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Mode toggle + Print buttons */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Modo:</span>
              <button onClick={() => setTimelineMode('dates')} className={`btn btn-xs ${timelineMode === 'dates' ? 'btn-primary' : 'btn-outline'}`}>
                Por Datas
              </button>
              <button onClick={() => setTimelineMode('days')} className={`btn btn-xs ${timelineMode === 'days' ? 'btn-primary' : 'btn-outline'}`}>
                Por Dias
              </button>
            </div>
            {project.timeline?.length > 0 && (
              <div className="flex gap-1">
                <button onClick={handlePrintCronogramaWindow} className="btn btn-outline btn-xs" title="Imprimir cronograma">
                  <Printer size={12} /> Imprimir
                </button>
                <button onClick={() => handlePrintCronograma('obra')} className="btn btn-outline btn-xs" title="PDF Chefe de Obra">
                  <Download size={12} /> PDF Obra
                </button>
                <button onClick={() => handlePrintCronograma('cliente')} className="btn btn-outline btn-xs" title="PDF Cliente">
                  <Download size={12} /> PDF Cliente
                </button>
              </div>
            )}
          </div>

          <h3 className="text-sm font-semibold mb-3">Nova Tarefa</h3>
          {timelineMode === 'dates' ? (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              <input className="input md:col-span-2" placeholder="Tarefa" value={taskForm.task} onChange={e => setTaskForm(p => ({ ...p, task: e.target.value }))} />
              <input className="input" type="date" placeholder="Inicio" value={taskForm.start_date} onChange={e => setTaskForm(p => ({ ...p, start_date: e.target.value }))} />
              <input className="input" type="date" placeholder="Fim" value={taskForm.end_date} onChange={e => setTaskForm(p => ({ ...p, end_date: e.target.value }))} />
              <button data-testid="add-timeline-task" onClick={addTask} className="btn btn-primary"><Plus size={14} /> Tarefa</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <input className="input md:col-span-2" placeholder="Nome da tarefa" value={taskForm.task} onChange={e => setTaskForm(p => ({ ...p, task: e.target.value }))} />
              <input className="input" type="number" min="1" placeholder="Numero de dias" value={taskForm.estimated_days || ''} onChange={e => setTaskForm(p => ({ ...p, estimated_days: parseInt(e.target.value) || 0 }))} />
              <button data-testid="add-timeline-task" onClick={addTask} className="btn btn-primary"><Plus size={14} /> Tarefa</button>
            </div>
          )}
          {timelineMode === 'days' && (
            <p className="text-[10px] mb-3" style={{ color: 'var(--text-secondary)' }}>
              As datas sao calculadas automaticamente a partir da data de inicio do projeto ({project.start_date || 'nao definida'}) + soma dos dias das tarefas anteriores.
            </p>
          )}

          {/* Visual timeline bars */}
          {project.timeline?.length > 0 ? (
            <div className="space-y-2">
              {project.timeline.map(t => {
                const hasDateRange = t.start_date && t.end_date;
                let taskDays = 0, taskPct = 0;
                if (hasDateRange) {
                  const s = new Date(t.start_date);
                  const e = new Date(t.end_date);
                  taskDays = Math.ceil((e - s) / (1000 * 60 * 60 * 24));
                  taskPct = t.status === 'concluido' ? 100 : (t.progress || 0);
                }
                return (
                  <div key={t.id} className="p-3 rounded-md" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{t.task}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <div className="flex items-center gap-1">
                            <label className="text-[9px]" style={{ color: 'var(--text-light)' }}>Inicio:</label>
                            <input 
                              type="date" 
                              className="input text-[10px] py-0.5 px-1" 
                              value={t.start_date || ''} 
                              onChange={async (e) => {
                                await api.put(`/projects/${project.id}/timeline/${t.id}`, { start_date: e.target.value });
                                refresh();
                              }}
                              style={{ width: 'auto' }}
                            />
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-[9px]" style={{ color: 'var(--text-light)' }}>Fim:</label>
                            <input 
                              type="date" 
                              className="input text-[10px] py-0.5 px-1" 
                              value={t.end_date || ''} 
                              onChange={async (e) => {
                                await api.put(`/projects/${project.id}/timeline/${t.id}`, { end_date: e.target.value });
                                refresh();
                              }}
                              style={{ width: 'auto' }}
                            />
                          </div>
                          {taskDays > 0 && <span className="text-[10px] font-medium" style={{ color: 'var(--text-secondary)' }}>({taskDays} dias)</span>}
                        </div>
                        {hasDateRange && (
                          <div className="mt-1.5 w-full h-1.5 rounded-full" style={{ background: 'var(--border)' }}>
                            <div className="h-1.5 rounded-full transition-all" style={{
                              width: `${taskPct}%`,
                              background: t.status === 'concluido' ? 'var(--success)' : t.status === 'em_curso' ? 'var(--warning)' : 'var(--border)'
                            }} />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <select className="select" style={{ width: 'auto', fontSize: '0.6875rem', padding: '0.25rem 0.5rem' }}
                          value={t.status} onChange={e => updateTaskStatus(t.id, e.target.value, e.target.value === 'concluido' ? 100 : t.progress)}>
                          <option value="pendente">Pendente</option><option value="em_curso">Em Curso</option><option value="concluido">Concluido</option>
                        </select>
                        <span className={`badge ${t.status === 'concluido' ? 'badge-success' : t.status === 'em_curso' ? 'badge-warning' : 'badge-neutral'}`}>{taskPct}%</span>
                        <button 
                          onClick={async () => {
                            if (window.confirm('Apagar tarefa?')) {
                              await api.delete(`/projects/${project.id}/timeline/${t.id}`);
                              refresh();
                            }
                          }}
                          className="btn btn-danger btn-xs"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem tarefas</p>}

          {/* Gantt Chart Visual */}
          {project.timeline?.length > 0 && (() => {
            const tasksWithDates = project.timeline.filter(t => t.start_date && t.end_date);
            if (tasksWithDates.length === 0) return null;
            const allStarts = tasksWithDates.map(t => new Date(t.start_date).getTime());
            const allEnds = tasksWithDates.map(t => new Date(t.end_date).getTime());
            const minDate = Math.min(...allStarts);
            const maxDate = Math.max(...allEnds);
            const totalRange = maxDate - minDate || 1;

            const statusColor = (s) => s === 'concluido' ? '#0D7C3E' : s === 'em_curso' ? '#C67A09' : '#94a3b8';
            const statusBg = (s) => s === 'concluido' ? '#d1fae5' : s === 'em_curso' ? '#fef3c7' : '#f1f5f9';

            return (
              <div className="mt-6" data-testid="gantt-chart">
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Gantt Chart</h3>
                <div className="overflow-x-auto">
                  <div style={{ minWidth: '500px' }}>
                    {/* Date header */}
                    <div className="flex items-center justify-between mb-1 px-1">
                      <span className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>{new Date(minDate).toLocaleDateString('pt-PT')}</span>
                      <span className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>{new Date(minDate + totalRange * 0.25).toLocaleDateString('pt-PT')}</span>
                      <span className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>{new Date(minDate + totalRange * 0.5).toLocaleDateString('pt-PT')}</span>
                      <span className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>{new Date(minDate + totalRange * 0.75).toLocaleDateString('pt-PT')}</span>
                      <span className="text-[10px] font-medium" style={{ color: 'var(--text-light)' }}>{new Date(maxDate).toLocaleDateString('pt-PT')}</span>
                    </div>
                    {/* Bars */}
                    <div className="space-y-1.5">
                      {tasksWithDates.map(t => {
                        const start = new Date(t.start_date).getTime();
                        const end = new Date(t.end_date).getTime();
                        const left = ((start - minDate) / totalRange) * 100;
                        const width = Math.max(2, ((end - start) / totalRange) * 100);
                        return (
                          <div key={t.id} className="flex items-center gap-2">
                            <div className="w-28 flex-shrink-0 truncate text-xs font-medium" title={t.task}>{t.task}</div>
                            <div className="flex-1 relative h-6 rounded" style={{ background: '#f1f5f9' }}>
                              <div className="absolute h-full rounded flex items-center px-1.5 transition-all" style={{
                                left: `${left}%`, width: `${width}%`,
                                background: statusBg(t.status), border: `1px solid ${statusColor(t.status)}`
                              }}>
                                <span className="text-[9px] font-semibold truncate" style={{ color: statusColor(t.status) }}>
                                  {t.status === 'concluido' ? '100%' : `${t.progress || 0}%`}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Legend */}
                    <div className="flex gap-4 mt-3">
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: '#d1fae5', border: '1px solid #0D7C3E' }} /><span className="text-[10px]">Concluido</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: '#fef3c7', border: '1px solid #C67A09' }} /><span className="text-[10px]">Em Curso</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded" style={{ background: '#f1f5f9', border: '1px solid #94a3b8' }} /><span className="text-[10px]">Pendente</span></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* PHOTOS */}
      {tab === 'photos' && (
        <div className="pro-card-padded">
          <div className="flex flex-wrap gap-2 mb-3 items-end">
            <input className="input flex-1 min-w-[150px]" placeholder="Legenda" value={photoCaption} onChange={e => setPhotoCaption(e.target.value)} />
            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input type="checkbox" checked={photoVisible} onChange={e => setPhotoVisible(e.target.checked)} className="w-4 h-4 rounded" /> Visivel cliente
            </label>
            <label className="btn btn-primary btn-sm cursor-pointer"><Camera size={14} /> Upload<input type="file" accept="image/*" className="hidden" onChange={addPhoto} /></label>
          </div>
          {project.photos?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {project.photos.map(p => (
                <div key={p.id} className="pro-card overflow-hidden group relative">
                  {p.image_base64 && <img src={`data:image/jpeg;base64,${p.image_base64}`} alt={p.caption} className="w-full h-28 object-cover" />}
                  <div className="p-2">
                    <input 
                      className="input text-xs w-full mb-1" 
                      defaultValue={p.caption || ''} 
                      placeholder="Legenda..."
                      onBlur={async (e) => {
                        if (e.target.value !== p.caption) {
                          await api.put(`/projects/${project.id}/photos/${p.id}`, { caption: e.target.value });
                          refresh();
                        }
                      }}
                    />
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={p.client_visible || false} 
                            onChange={async (e) => {
                              await api.put(`/projects/${project.id}/photos/${p.id}`, { client_visible: e.target.checked });
                              refresh();
                            }}
                            className="w-3 h-3 rounded" 
                          /> Cliente
                        </label>
                        <span className="text-[9px]" style={{ color: 'var(--text-light)' }}>{p.uploaded_by}</span>
                      </div>
                      <button 
                        onClick={async () => {
                          if (window.confirm('Apagar foto?')) {
                            await api.delete(`/projects/${project.id}/photos/${p.id}`);
                            refresh();
                          }
                        }}
                        className="btn btn-danger btn-xs"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem fotos</p>}
        </div>
      )}

      {/* FILES */}
      {tab === 'files' && (
        <div className="pro-card-padded">
          <div className="flex gap-2 mb-3 items-center">
            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input type="checkbox" checked={fileVisible} onChange={e => setFileVisible(e.target.checked)} className="w-4 h-4 rounded" /> Visivel cliente
            </label>
            <label className="btn btn-primary btn-sm cursor-pointer"><FileUp size={14} /> Upload Ficheiro<input type="file" className="hidden" onChange={addFile} /></label>
          </div>
          {project.files?.length > 0 ? (
            <div className="space-y-2">
              {project.files.map(f => (
                <div key={f.id} className="flex items-center justify-between p-3 rounded-md" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}>
                  <div className="flex items-center gap-2 flex-1">
                    <Paperclip size={14} style={{ color: 'var(--text-light)' }} />
                    <div className="flex-1">
                      <input 
                        className="input text-sm font-medium w-full"
                        defaultValue={f.name}
                        onBlur={async (e) => {
                          if (e.target.value !== f.name) {
                            await api.put(`/projects/${project.id}/files/${f.id}`, { name: e.target.value });
                            refresh();
                          }
                        }}
                      />
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-light)' }}>{f.uploaded_by} - {f.uploaded_at}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-[10px] cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={f.client_visible || false} 
                        onChange={async (e) => {
                          await api.put(`/projects/${project.id}/files/${f.id}`, { client_visible: e.target.checked });
                          refresh();
                        }}
                        className="w-3 h-3 rounded" 
                      /> Cliente
                    </label>
                    <button 
                      onClick={async () => {
                        if (window.confirm('Apagar ficheiro?')) {
                          await api.delete(`/projects/${project.id}/files/${f.id}`);
                          refresh();
                        }
                      }}
                      className="btn btn-danger btn-xs"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem ficheiros</p>}
        </div>
      )}

      {/* MESSAGES */}
      {tab === 'messages' && (
        <div className="pro-card-padded">
          {/* Mark messages as read button */}
          {project.messages?.some(m => m.author_type === 'client' && !m.read_by_staff) && (
            <div className="flex justify-end mb-2">
              <button 
                onClick={async () => {
                  await api.post(`/projects/${project.id}/messages/mark-read`);
                  refresh();
                }}
                className="btn btn-outline btn-xs"
              >
                Marcar todas como lidas
              </button>
            </div>
          )}
          <div className="max-h-80 overflow-y-auto scrollbar-thin mb-3 space-y-2">
            {project.messages?.length > 0 ? project.messages.map(m => {
              // Can edit/delete only if not read by the other party
              const canEditDelete = m.author_type === 'staff' ? !m.read_by_client : !m.read_by_staff;
              const isRead = m.author_type === 'staff' ? m.read_by_client : m.read_by_staff;
              
              return (
                <div key={m.id} className={`flex ${m.author_type === 'client' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`msg-bubble ${m.author_type === 'client' ? 'msg-bubble-other' : 'msg-bubble-self'} relative group`}>
                    <div className="flex items-center gap-2 justify-between mb-0.5">
                      <p className="text-[10px] font-semibold opacity-70">
                        {m.author}{!m.client_visible && ' (interno)'}
                      </p>
                      <div className="flex items-center gap-1">
                        {isRead && <span className="text-[9px]" style={{ color: 'var(--success)' }}>✓ Lida</span>}
                        {canEditDelete && (
                          <>
                            <button 
                              onClick={async () => {
                                const newText = window.prompt('Editar mensagem:', m.text);
                                if (newText && newText !== m.text) {
                                  try {
                                    await api.put(`/projects/${project.id}/messages/${m.id}`, { text: newText });
                                    refresh();
                                  } catch (err) {
                                    alert(err.response?.data?.detail || 'Erro ao editar');
                                  }
                                }
                              }}
                              className="text-[10px] opacity-60 hover:opacity-100"
                              title="Editar"
                            >
                              <Edit3 size={10} />
                            </button>
                            <button 
                              onClick={async () => {
                                if (window.confirm('Apagar mensagem?')) {
                                  try {
                                    await api.delete(`/projects/${project.id}/messages/${m.id}`);
                                    refresh();
                                  } catch (err) {
                                    alert(err.response?.data?.detail || 'Erro ao apagar');
                                  }
                                }
                              }}
                              className="text-[10px] opacity-60 hover:opacity-100"
                              title="Apagar"
                            >
                              <Trash2 size={10} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    <p className="text-sm">{m.text}</p>
                    <p className="text-[9px] mt-1 opacity-50">{m.created_at}</p>
                  </div>
                </div>
              );
            }) : <p className="text-xs text-center py-6" style={{ color: 'var(--text-light)' }}>Sem mensagens</p>}
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <input className="input" placeholder="Escrever mensagem..." value={msgText} onChange={e => setMsgText(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} />
              <label className="flex items-center gap-1 text-[10px] font-medium mt-1 cursor-pointer" style={{ color: 'var(--text-light)' }}>
                <input type="checkbox" checked={msgVisible} onChange={e => setMsgVisible(e.target.checked)} className="w-3 h-3 rounded" /> Visivel ao cliente
              </label>
            </div>
            <button data-testid="send-msg-btn" onClick={sendMessage} className="btn btn-primary"><Send size={14} /></button>
          </div>
        </div>
      )}

      {/* PORTAL */}
      {tab === 'portal' && (
        <div className="pro-card-padded">
          <h3 className="text-sm font-semibold mb-3">Dados de Acesso do Cliente</h3>
          <div className="p-4 rounded-md mb-4" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>
            <div className="mb-3">
              <p className="field-label">Link de Acesso</p>
              <code className="text-xs font-mono block p-2 rounded" style={{ background: '#fff', border: '1px solid var(--border)' }}>
                {window.location.origin}/portal/{project.id}
              </code>
            </div>
            <div>
              <p className="field-label">Senha</p>
              <code className="text-xs font-mono block p-2 rounded" style={{ background: '#fff', border: '1px solid var(--border)' }}>
                {project.client_portal_password}
              </code>
            </div>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            O cliente pode ver fotos/ficheiros marcados como visiveis, mensagens visiveis, cronograma, e o orcamento. Pode tambem enviar mensagens e fotos.
          </p>
        </div>
      )}
    </div>
  );
}

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', client_name: '', client_email: '', location: '', description: '', budget_total: 0, start_date: '' });
  const [showAssociate, setShowAssociate] = useState(null);

  const load = useCallback(() => {
    api.get('/projects').then(r => setProjects(r.data)).catch(() => {});
    api.get('/employees').then(r => setEmployees(r.data)).catch(() => {});
    api.get('/vehicles').then(r => setVehicles(r.data)).catch(() => {});
    api.get('/budgets').then(r => setBudgets(r.data.filter(b => b.status === 'aprovado' && !b.is_project))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => { if (window.confirm('Eliminar?')) { await api.delete(`/projects/${id}`); load(); } };

  const handleCreateProject = async () => {
    try {
      await api.post('/projects', newProject);
      setShowCreate(false);
      setNewProject({ name: '', client_name: '', client_email: '', location: '', description: '', budget_total: 0, start_date: '' });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao criar projeto');
    }
  };

  const handleAssociateBudget = async (projectId, budgetId) => {
    try {
      await api.post(`/projects/${projectId}/associate-budget`, { budget_id: budgetId });
      setShowAssociate(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao associar orcamento');
    }
  };

  const filtered = projects.filter(p => !filterStatus || p.status === filterStatus);

  if (selected) {
    return <ProjectDetail project={selected} onBack={() => { setSelected(null); load(); }} employees={employees} vehicles={vehicles} />;
  }

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">Gestao de Obra</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{projects.length} projeto{projects.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <select data-testid="project-filter-status" className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">Todos os estados</option>
            <option value="planeamento">Planeamento</option>
            <option value="em_curso">Em Curso</option>
            <option value="concluido">Concluido</option>
            <option value="suspenso">Suspenso</option>
          </select>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
            <Plus size={14} /> Nova Obra
          </button>
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="pro-card-padded w-full max-w-lg animate-in">
            <h2 className="text-lg font-bold mb-4">Nova Obra (Manual)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div className="md:col-span-2">
                <label className="field-label">Nome da Obra *</label>
                <input className="input" placeholder="Ex: Remodelacao Apartamento T3" value={newProject.name} onChange={e => setNewProject(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className="field-label">Cliente</label>
                <input className="input" placeholder="Nome do cliente" value={newProject.client_name} onChange={e => setNewProject(p => ({ ...p, client_name: e.target.value }))} />
              </div>
              <div>
                <label className="field-label">Email Cliente</label>
                <input className="input" type="email" placeholder="email@cliente.pt" value={newProject.client_email} onChange={e => setNewProject(p => ({ ...p, client_email: e.target.value }))} />
              </div>
              <div>
                <label className="field-label">Localizacao</label>
                <input className="input" placeholder="Morada" value={newProject.location} onChange={e => setNewProject(p => ({ ...p, location: e.target.value }))} />
              </div>
              <div>
                <label className="field-label">Orcamento Estimado (EUR)</label>
                <input className="input" type="number" placeholder="0" value={newProject.budget_total || ''} onChange={e => setNewProject(p => ({ ...p, budget_total: parseFloat(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className="field-label">Data Inicio</label>
                <input className="input" type="date" value={newProject.start_date} onChange={e => setNewProject(p => ({ ...p, start_date: e.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Descricao</label>
                <textarea className="input" rows={2} placeholder="Descricao do trabalho..." value={newProject.description} onChange={e => setNewProject(p => ({ ...p, description: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="btn btn-outline">Cancelar</button>
              <button onClick={handleCreateProject} className="btn btn-primary" disabled={!newProject.name}>Criar Obra</button>
            </div>
          </div>
        </div>
      )}

      {/* Associate Budget Modal */}
      {showAssociate && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="pro-card-padded w-full max-w-md animate-in">
            <h2 className="text-lg font-bold mb-4">Associar Orcamento</h2>
            <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
              Selecione um orcamento aprovado para associar a esta obra. Os itens e custos serao importados.
            </p>
            {budgets.length > 0 ? (
              <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                {budgets.map(b => (
                  <div 
                    key={b.id} 
                    className="p-3 rounded-md cursor-pointer hover:shadow-md transition-shadow"
                    style={{ background: 'var(--surface-alt)', border: '1px solid var(--border-light)' }}
                    onClick={() => handleAssociateBudget(showAssociate.id, b.id)}
                  >
                    <p className="text-sm font-semibold">{b.description || b.id}</p>
                    <p className="text-xs" style={{ color: 'var(--text-light)' }}>{b.client_name} - {b.total?.toLocaleString('pt-PT')} EUR</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs py-4 text-center" style={{ color: 'var(--text-light)' }}>Nenhum orcamento aprovado disponivel</p>
            )}
            <button onClick={() => setShowAssociate(null)} className="btn btn-outline w-full">Fechar</button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="pro-card-padded text-center py-12">
          <Building2 size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} />
          <p className="font-semibold">Sem projetos</p>
          <p className="text-xs mt-1 mb-4" style={{ color: 'var(--text-secondary)' }}>Crie uma nova obra ou converta um orcamento</p>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary">
            <Plus size={16} /> Criar Obra
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(p => {
            const st = STATUS_MAP[p.status] || STATUS_MAP.planeamento;
            const hasBudget = !!p.budget_id;
            return (
              <div key={p.id} data-testid={`project-card-${p.id}`} className="pro-card-padded cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelected(p)}>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold truncate flex-1">{p.name}</h3>
                  <span className={`badge ${st.cls} ml-2`}>{st.label}</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{p.client_name}</p>
                <p className="text-xs mb-2" style={{ color: 'var(--text-light)' }}>{p.location}</p>
                {!hasBudget && (
                  <button 
                    onClick={e => { e.stopPropagation(); setShowAssociate(p); }}
                    className="btn btn-outline btn-xs mb-2 w-full"
                  >
                    <Plus size={12} /> Associar Orcamento
                  </button>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{(p.budget_total || 0).toLocaleString('pt-PT')} EUR</span>
                  <button data-testid={`delete-project-${p.id}`} onClick={e => { e.stopPropagation(); handleDelete(p.id); }} className="btn btn-danger btn-xs"><Trash2 size={12} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
